import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getTodayDateFormatted } from 'helpers/helpers';
import { getAreaSize } from 'services/filterService';
import { TimeInMS } from 'types/time';

import {
  getBuildingDemandSummary,
  getEfficiancyRangeDataOfType,
  getHomePageInfo,
  getImpactDataAPI,
  getRealTimeData,
} from '../../services/dataService';

export const fetchAreaSize = createAsyncThunk('api/buildings/area-size', async (params, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const building_id = state.persistence.selectedFilterObject.building; //from sensor state
    const floor_number = state.persistence.selectedFilterObject.floor; //from sensor state

    if (!building_id || building_id === 'undefined' || building_id === 'all') {
      throw new Error('No specific building selected for real-time data');
    }

    let res;

    if (floor_number === 'all') {
      res = await getAreaSize(building_id);
    } else {
      res = await getAreaSize(building_id, floor_number);
    }
    return res;
  } catch (err) {
    return rejectWithValue(err?.message || err);
  }
});

export const fetchRealTimeData = createAsyncThunk('homePage/fetchRealTimeData', async (params, { getState, rejectWithValue }) => {
  try {
    const { floor_number } = params || {};

    const state = getState();
    const building_id = state.persistence.selectedFilterObject.building; //from state
    const device_ids = state.sensors.filteredDevicesIds; //from state

    if (!building_id || building_id === 'undefined' || building_id === 'all') {
      throw new Error('No specific building selected for real-time data');
    }

    // Pass both values from Redux state
    const res = await getRealTimeData(building_id, floor_number, device_ids);
    return res;
  } catch (err) {
    return rejectWithValue(err?.message || err);
  }
});

// export const fetchEfficiancyData = createAsyncThunk(
//   'homePage/fetchEfficiancyData',
//   async (params, { getState, rejectWithValue }) => {
//     try {
//       const { type, forHomePage = false, timeRange, userTimeZone = 'Asia/Jerusalem' } = params;

//       if (!type) {
//         throw new Error('Missing type parameter for efficiency data');
//       }

//       const state = getState();
//       const building_id = state.persistence.selectedFilterObject.building; //from redux
//       const device_ids = state.sensors.filteredDevicesIds; //from redux

//       // Validate building
//       if (!building_id || building_id === 'undefined' || building_id === 'all') {
//         throw new Error('No specific building selected for efficiency data');
//       }

//       // Use timeRange or fall back to datesObj for backward compatibility
//       const dates = timeRange || state.homePage.homePageDatesObj;

//       const res = await getEfficiancyRangeDataOfType(forHomePage, type, building_id, dates, device_ids);
//       return { type, data: res };
//     } catch (err) {
//       return rejectWithValue(err?.message || err);
//     }
//   },
// );

export const fetchImpactData = createAsyncThunk('homePage/fetchImpactData', async (params, { getState, rejectWithValue }) => {
  try {
    const { building_id, floor_number, hvacEquipment, timeRange, userTimeZone = 'Asia/Jerusalem' } = params;

    const res = await getImpactDataAPI(building_id, floor_number, timeRange, userTimeZone, hvacEquipment);
    return res;
  } catch (err) {
    return rejectWithValue(err?.message || err);
  }
});

export const fetchHomePageInfo = createAsyncThunk('homePage/fetchHomePageInfo', async (params, { rejectWithValue }) => {
  try {
    const { building_id, floor_number, timeRange, columns, userTimeZone = 'Asia/Jerusalem' } = params;

    const res = await getHomePageInfo(building_id, floor_number, timeRange, columns, userTimeZone);
    return res;
  } catch (err) {
    return rejectWithValue(err?.message || err);
  }
});

export const fetchBuildingDemandSummary = createAsyncThunk(
  'homePage/fetchBuildingDemandSummary',
  async (filters, { rejectWithValue }) => {
    try {
      return await getBuildingDemandSummary(filters);
    } catch (err) {
      return rejectWithValue(err?.message || err);
    }
  },
);

const homePageInitialState = {
  iaqRealTime: undefined,
  ocpRealTime: undefined,
  realTimeLoading: false,
  freshAirNumOfHours: undefined,
  hvacNumOfHours: undefined,
  freshAirLoading: false,
  hvacLoading: false,
  cav_hours: {
    start: '07:00:00',
    end: '22:00:00',
  },
  homePageDatesObj: {
    startDate: getTodayDateFormatted(),
    endDate: getTodayDateFormatted(),
    label: 'Today',
    value: TimeInMS.Day,
  },
  areaSize: null,
  areaSizeLoading: false,
  impactData: null,
  impactLoading: false,
  impactError: null,
};

export const homePageSlice = createSlice({
  name: 'homePage',
  initialState: homePageInitialState,
  reducers: {
    clear: state => {
      state.homePageInitialState = homePageInitialState;
    },
    applySetPointsLoading: (state, action) => {
      state.realTimeLoading = action.payload;
    },
    setFreshAirLoading: (state, action) => {
      state.freshAirLoading = action.payload;
    },
    setHvacLoading: (state, action) => {
      state.hvacLoading = action.payload;
    },
    setHomePageDatesObj: (state, action) => {
      state.homePageDatesObj = action.payload;
    },
    setAreaSizeLoading: (state, action) => {
      state.areaSizeLoading = action.payload;
    },
  },
  extraReducers: {
    // Real-time data cases
    [fetchRealTimeData.pending]: state => {
      state.realTimeLoading = true;
    },
    [fetchRealTimeData.fulfilled]: (state, action) => {
      try {
        if (action.payload?.message) {
          state.iaqRealTime = null;
          state.ocpRealTime = null;
        } else {
          state.iaqRealTime = [action.payload.iaq, 100 - action.payload.iaq];
          state.ocpRealTime = [action.payload.ocp, 100 - action.payload.ocp];
        }
        state.realTimeLoading = false;
      } catch (error) {
        console.error('Real-time data processing error:', error);
        state.realTimeLoading = false;
      }
    },
    [fetchRealTimeData.rejected]: (state, action) => {
      state.realTimeLoading = false;
      state.iaqRealTime = null;
      state.ocpRealTime = null;
      console.error('Real-time data fetch failed:', action.error);
    },

    // // Efficiency data cases
    // [fetchEfficiancyData.pending]: state => {
    //   // Loading states are managed by individual action calls
    // },
    // [fetchEfficiancyData.fulfilled]: (state, action) => {
    //   console.log('🔍 Efficiency data received:', action.payload); // ADD THIS LINE

    //   try {
    //     if (!action.payload || action.payload.data?.message) {
    //       // Handle no data case
    //       if (action.payload?.type === 'co2') {
    //         state.freshAirNumOfHours = null;
    //         state.freshAirLoading = false;
    //       } else if (action.payload?.type === 'temperature') {
    //         state.hvacNumOfHours = null;
    //         state.hvacLoading = false;
    //       }
    //     } else {
    //       // Handle successful data
    //       const { type, data } = action.payload;

    //       if (type === 'co2') {
    //         state.freshAirNumOfHours = data.sumOfAvgs;
    //         state.freshAirLoading = false;
    //       } else if (type === 'temperature') {
    //         state.hvacNumOfHours = data.sumOfAvgs;
    //         state.hvacLoading = false;
    //       }
    //     }
    //   } catch (error) {
    //     console.error('Efficiency data processing error:', error);
    //     state.freshAirLoading = false;
    //     state.hvacLoading = false;
    //   }
    // },
    // [fetchEfficiancyData.rejected]: (state, action) => {
    //   state.freshAirLoading = false;
    //   state.hvacLoading = false;
    //   state.freshAirNumOfHours = null;
    //   state.hvacNumOfHours = null;
    //   console.error('Efficiency data fetch failed:', action.error);
    // },

    // Area size cases
    [fetchAreaSize.pending]: state => {
      state.areaSizeLoading = true;
    },
    [fetchAreaSize.fulfilled]: (state, action) => {
      state.areaSize = action.payload;
      state.areaSizeLoading = false;
    },
    // Impact data cases
    [fetchImpactData.pending]: state => {
      state.impactLoading = true;
      state.impactError = null;
    },
    [fetchImpactData.fulfilled]: (state, action) => {
      state.impactLoading = false;
      state.impactData = action.payload?.data ?? action.payload;
      state.impactError = null;
    },
    [fetchImpactData.rejected]: (state, action) => {
      state.impactLoading = false;
      state.impactData = null;
      state.impactError = action.payload || action.error?.message || 'Failed to fetch impact data';
      console.error('Impact data fetch failed:', action.error);
    },
    // [fetchEfficiancyData.fulfilled]: (state, action) => {
    //   if (!action.payload || action.payload.data.message) {
    //     state.freshAirLoading = false;
    //     state.hvacLoading = false;
    //     state.freshAirNumOfHours = null;
    //     state.hvacNumOfHours = null;
    //     return;
    //   }

    //   if (action.payload.data.type === 'co2_max') {
    //     state.freshAirNumOfHours = action.payload.data.sumOfAvgs;
    //   } else {
    //     state.hvacNumOfHours = action.payload.data.sumOfAvgs;
    //   }
    //   state.freshAirLoading = false;
    //   state.hvacLoading = false;
    // },
    [fetchAreaSize.rejected]: (state, action) => {
      state.areaSizeLoading = false;
      state.areaSize = null;
      console.error('Area size fetch failed:', action.error);
    },
  },
});

export const { setFreshAirLoading, setHvacLoading, applySetPointsLoading, setHomePageDatesObj, setAreaSizeLoading } =
  homePageSlice.actions;
